import React, { useState } from 'react';
import { SaibaMais } from './SaibaMais';
import {
  Shield, Server, Mail, HardDrive, Globe, Lock, HeadphonesIcon,
  CheckCircle2, Star, Zap, ChevronDown, ChevronUp, ArrowRight,
  Clock, Award, Wifi, Database, RefreshCw
} from 'lucide-react';

const WHATSAPP_HOSTING = 'https://wa.me/5516991378312?text=' + encodeURIComponent('Olá! Quero contratar hospedagem de site com a Fernando Tech. Pode me ajudar?');
const WHATSAPP_SPECIALIST = 'https://wa.me/5516991378312?text=' + encodeURIComponent('Olá! Quero falar com um especialista sobre hospedagem empresarial.');

const plans = [
  {
    id: 'start',
    name: 'Start',
    price: '29,90',
    period: '/mês',
    description: 'Ideal para pequenos negócios e sites institucionais.',
    highlight: false,
    badge: null,
    color: 'cyan',
    features: [
      { icon: Mail, text: '2 contas de e-mail profissional' },
      { icon: HardDrive, text: '2GB de armazenamento SSD' },
      { icon: Globe, text: '1 domínio incluso' },
      { icon: Lock, text: 'Certificado SSL incluso' },
      { icon: RefreshCw, text: 'Backup mensal automático' },
      { icon: HeadphonesIcon, text: 'Suporte técnico' },
    ],
  },
  {
    id: 'business',
    name: 'Business',
    price: '49,90',
    period: '/mês',
    description: 'Para empresas que precisam de mais performance e segurança.',
    highlight: true,
    badge: 'Mais Vendido',
    color: 'amber',
    features: [
      { icon: Mail, text: '5 contas de e-mail profissional' },
      { icon: HardDrive, text: '5GB de armazenamento SSD' },
      { icon: Globe, text: '1 domínio incluso' },
      { icon: Lock, text: 'Certificado SSL incluso' },
      { icon: RefreshCw, text: 'Backup semanal automático' },
      { icon: Shield, text: 'Configuração SPF, DKIM e segurança' },
      { icon: HeadphonesIcon, text: 'Suporte prioritário' },
    ],
  },
  {
    id: 'empresarial',
    name: 'Empresarial',
    price: '89,90',
    period: '/mês',
    description: 'Solução completa para empresas que não aceitam falhas.',
    highlight: false,
    badge: null,
    color: 'blue',
    features: [
      { icon: Mail, text: '10 contas de e-mail profissional' },
      { icon: HardDrive, text: '10GB de armazenamento SSD' },
      { icon: Globe, text: '1 domínio incluso' },
      { icon: Lock, text: 'Certificado SSL incluso' },
      { icon: RefreshCw, text: 'Backup semanal automático' },
      { icon: Database, text: 'Monitoramento básico' },
      { icon: Shield, text: 'Configuração completa de segurança' },
      { icon: HeadphonesIcon, text: 'Suporte dedicado' },
    ],
  },
];

const diferenciais = [
  { icon: HeadphonesIcon, title: 'Suporte Técnico Especializado', desc: 'Atendimento humano e rápido via WhatsApp, sem chatbots ou espera longa.' },
  { icon: Shield, title: 'Segurança Contra Spam', desc: 'Configuração SPF, DKIM e DMARC para proteger seu e-mail corporativo.' },
  { icon: Zap, title: 'Configuração Profissional', desc: 'Instalação, migração e configuração completa feita por especialistas.' },
  { icon: Clock, title: 'Atendimento Rápido', desc: 'Respondemos em minutos pelo WhatsApp, não em dias como o suporte tradicional.' },
  { icon: Award, title: 'Empresa Local Confiável', desc: 'Baseados em Ribeirão Preto — você fala com quem realmente entende do seu negócio.' },
  { icon: Wifi, title: 'Servidor Estável', desc: 'Infraestrutura de alto desempenho com uptime garantido para sua presença online.' },
];

const faqs = [
  {
    q: 'O que é hospedagem empresarial?',
    a: 'Hospedagem empresarial é um serviço de servidor otimizado para negócios que precisam de estabilidade, segurança avançada, e-mail corporativo e suporte técnico especializado. Diferente da hospedagem básica, ela inclui configurações profissionais como SPF, DKIM e backup periódico.',
  },
  {
    q: 'Qual a diferença entre hospedagem comum e profissional?',
    a: 'A hospedagem profissional oferece configuração técnica completa, segurança reforçada, backup automático, suporte especializado e e-mail corporativo configurado corretamente. A hospedagem comum geralmente entrega apenas o servidor, sem qualquer suporte na configuração ou manutenção.',
  },
  {
    q: 'A hospedagem inclui e-mail corporativo?',
    a: 'Sim! Todos os planos incluem contas de e-mail profissional com o seu domínio (ex: contato@suaempresa.com.br). Nos planos Business e Empresarial também realizamos a configuração de segurança SPF e DKIM para garantir a entrega correta dos seus e-mails.',
  },
  {
    q: 'Vocês oferecem suporte técnico?',
    a: 'Sim! Oferecemos suporte técnico especializado via WhatsApp com atendimento rápido e humano. Nos planos Business e Empresarial o atendimento é prioritário e dedicado, garantindo que problemas sejam resolvidos sem demora.',
  },
  {
    q: 'Posso migrar meu site atual?',
    a: 'Sim! Realizamos a migração completa do seu site atual para a nossa hospedagem sem custo adicional, sem tempo de inatividade e sem risco de perda de dados. Cuidamos de tudo para que a transição seja transparente para você e seus clientes.',
  },
];

export default function Hospedagem({ showLearnMore }) {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <section
      id="hospedagem"
      className="bg-slate-950 overflow-hidden"
      aria-label="Hospedagem de Site Profissional"
    >
      {/* ── HERO ───────────────────────────────────────────────── */}
      <div className="relative py-24 md:py-32">
        {/* Glow de fundo */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(circle at 50% 40%, rgba(251,191,36,0.10) 0%, transparent 65%)' }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          {/* Pill badge */}
          <span className="inline-flex items-center gap-2 bg-amber-400/10 border border-amber-500/30 text-amber-400 text-sm font-semibold px-4 py-2 rounded-full mb-6">
            <Server className="w-4 h-4" />
            Hospedagem Empresarial — Ribeirão Preto
          </span>

          {/* H2 SEO — seção da homepage */}
          <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight text-white leading-tight mb-6">
            Hospedagem de Site{' '}
            <span className="text-amber-400">Profissional</span>
            <br className="hidden md:block" /> com Suporte Técnico de Verdade
          </h2>

          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Estabilidade, segurança e <strong className="text-white">e-mail corporativo</strong> configurado por especialistas.
            Para empresas que não podem perder tempo com quedas ou problemas técnicos.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={WHATSAPP_HOSTING}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hosting-cta-primary"
              className="flex items-center gap-2 bg-amber-400 text-slate-950 font-bold px-8 py-4 rounded-xl shadow-[0_0_24px_rgba(251,191,36,0.35)] hover:shadow-[0_0_36px_rgba(251,191,36,0.55)] hover:bg-amber-300 transition-all duration-300 text-base"
            >
              <Server className="w-5 h-5" />
              Quero Hospedar Meu Site
            </a>
            <a
              href={WHATSAPP_SPECIALIST}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hosting-cta-secondary"
              className="flex items-center gap-2 border border-slate-600 text-slate-200 hover:border-amber-400/60 hover:text-amber-400 font-semibold px-8 py-4 rounded-xl transition-all duration-300 text-base"
            >
              Falar com Especialista
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 mt-12 text-slate-400 text-sm">
            {['SSL Grátis em todos os planos', 'Migração gratuita', 'Suporte via WhatsApp', 'Atendimento local — Ribeirão Preto'].map((t) => (
              <span key={t} className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-amber-400 flex-shrink-0" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── PLANOS ─────────────────────────────────────────────── */}
      <div className="py-20 md:py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Planos de{' '}
              <span className="text-amber-400">Hospedagem Empresarial</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Escolha o plano ideal para sua empresa. Todos incluem SSL, e-mail corporativo e suporte técnico especializado.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            {plans.map((plan) => (
              <div
                key={plan.id}
                data-testid={`hosting-plan-${plan.id}`}
                className={`relative rounded-2xl p-8 flex flex-col border transition-all duration-300 ${
                  plan.highlight
                    ? 'bg-gradient-to-b from-amber-400/10 to-slate-900 border-amber-400/50 shadow-[0_0_40px_rgba(251,191,36,0.15)] scale-105 z-10'
                    : 'bg-slate-900 border-slate-700/60 hover:border-slate-600'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="flex items-center gap-1.5 bg-amber-400 text-slate-950 text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
                      <Star className="w-3.5 h-3.5 fill-slate-950" />
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className={`text-xl font-bold mb-1 ${plan.highlight ? 'text-amber-400' : 'text-white'}`}>
                    {plan.name}
                  </h3>
                  <p className="text-slate-400 text-sm">{plan.description}</p>
                </div>

                <div className="mb-8">
                  <div className="flex items-end gap-1">
                    <span className="text-slate-400 text-sm font-medium self-start mt-2">R$</span>
                    <span className={`text-5xl font-extrabold tracking-tight ${plan.highlight ? 'text-amber-400' : 'text-white'}`}>
                      {plan.price.split(',')[0]}
                    </span>
                    <span className={`text-2xl font-bold mb-1 ${plan.highlight ? 'text-amber-400' : 'text-white'}`}>
                      ,{plan.price.split(',')[1]}
                    </span>
                    <span className="text-slate-400 text-sm mb-1">{plan.period}</span>
                  </div>
                  <p className="text-slate-500 text-xs mt-1">ou R$ {(parseFloat(plan.price.replace(',', '.')) * 12 * 0.90).toFixed(2).replace('.', ',')} /ano <span className="text-amber-400 font-semibold">(10% off)</span></p>
                </div>

                <ul className="space-y-3 flex-1 mb-8">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                      <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.highlight ? 'text-amber-400' : 'text-cyan-500'}`} />
                      {f.text}
                    </li>
                  ))}
                </ul>

                <a
                  href={`https://wa.me/5516991378312?text=${encodeURIComponent(`Olá! Tenho interesse no plano de hospedagem ${plan.name} (R$ ${plan.price}/mês). Pode me ajudar?`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`hosting-plan-cta-${plan.id}`}
                  className={`flex items-center justify-center gap-2 font-bold py-3.5 px-6 rounded-xl transition-all duration-200 text-sm ${
                    plan.highlight
                      ? 'bg-amber-400 text-slate-950 hover:bg-amber-300 shadow-[0_0_20px_rgba(251,191,36,0.25)]'
                      : 'bg-slate-800 text-white hover:bg-slate-700 border border-slate-700'
                  }`}
                >
                  Contratar Plano {plan.name}
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PLANO ANUAL ────────────────────────────────────────── */}
      <div className="py-14 bg-gradient-to-r from-amber-400/10 via-slate-950 to-amber-400/10 border-y border-amber-500/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Pague anual e{' '}
                <span className="text-amber-400">economize 10%</span>
              </h2>
              <p className="text-slate-400 text-base max-w-xl">
                Garanta previsibilidade financeira e pague menos. No plano Business anual, você economiza <strong className="text-white">R$ 59,88</strong> por ano.
                Ideal para empresas que querem estabilidade e controle de custos.
              </p>
            </div>
            <a
              href={WHATSAPP_HOSTING}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hosting-annual-cta"
              className="flex-shrink-0 flex items-center gap-2 bg-amber-400 text-slate-950 font-bold px-8 py-4 rounded-xl hover:bg-amber-300 transition-all shadow-[0_0_24px_rgba(251,191,36,0.3)] text-base whitespace-nowrap"
            >
              Contratar Plano Anual
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>

      {/* ── DIFERENCIAIS ───────────────────────────────────────── */}
      <div className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Por que escolher a{' '}
              <span className="text-amber-400">Fernando Tech</span>?
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Diferente das grandes empresas de hospedagem, oferecemos suporte técnico humano, local e especializado.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {diferenciais.map((d, i) => (
              <div
                key={i}
                className="bg-slate-900 border border-slate-800 hover:border-amber-500/30 rounded-2xl p-6 flex gap-4 items-start transition-colors duration-300 group"
              >
                <div className="bg-amber-400/10 group-hover:bg-amber-400/20 p-3 rounded-xl flex-shrink-0 transition-colors duration-300">
                  <d.icon className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{d.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── TEXTO SEO OTIMIZADO ─────────────────────────────────── */}
      <div className="py-16 bg-slate-900/50 border-y border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-invert max-w-none">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
              Hospedagem de Site Profissional em Ribeirão Preto com{' '}
              <span className="text-amber-400">Suporte Técnico Especializado</span>
            </h2>
            <p className="text-slate-300 text-base leading-relaxed mb-8">
              Se você busca <strong className="text-white">hospedagem de site profissional</strong> com estabilidade, segurança e suporte
              técnico de verdade, a <strong className="text-white">Fernando Tech</strong> oferece a solução ideal para empresas que precisam
              de desempenho e confiabilidade. Trabalhamos com <strong className="text-white">hospedagem empresarial</strong> voltada para
              negócios que utilizam <strong className="text-white">e-mail corporativo</strong>, precisam de servidor estável e não podem
              correr riscos com quedas ou falhas técnicas.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-bold text-white mb-3">Por que contratar hospedagem empresarial?</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-3">
                  Diferente da hospedagem comum, a <strong className="text-white">hospedagem empresarial</strong> oferece:
                </p>
                <ul className="space-y-2">
                  {['Maior segurança e proteção avançada', 'Configuração profissional completa', 'Proteção contra spam e phishing', 'Backup periódico automático', 'Suporte técnico especializado em Ribeirão Preto'].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-slate-300 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-3">Servidor de hospedagem confiável</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Na Fernando Tech, você não contrata apenas um <strong className="text-white">servidor de hospedagem confiável</strong>.
                  Você contrata instalação e configuração completa, migração de site sem downtime, configuração de e-mails corporativos,
                  segurança e proteção, e atendimento rápido via WhatsApp para empresas locais de Ribeirão Preto e região.
                </p>
              </div>
            </div>
          </article>
        </div>
      </div>

      {/* ── FAQ ─────────────────────────────────────────────────── */}
      <div className="py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Perguntas Frequentes sobre{' '}
              <span className="text-amber-400">Hospedagem</span>
            </h2>
            <p className="text-slate-400">Tire suas dúvidas antes de contratar.</p>
          </div>

          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {faqs.map((faq, i) => (
              <div
                key={i}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                className={`border rounded-xl overflow-hidden transition-colors duration-200 ${
                  openFaq === i ? 'border-amber-500/40 bg-amber-400/5' : 'border-slate-800 bg-slate-900'
                }`}
                data-testid={`hosting-faq-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                  aria-expanded={openFaq === i}
                >
                  <span itemProp="name" className="text-white font-semibold pr-4">{faq.q}</span>
                  {openFaq === i
                    ? <ChevronUp className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    className="px-6 pb-5"
                  >
                    <p itemProp="text" className="text-slate-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA FINAL ───────────────────────────────────────────── */}
      <div className="py-20 bg-gradient-to-b from-slate-900 to-slate-950 border-t border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-400/10 border border-amber-500/30 text-amber-400 text-sm font-semibold px-4 py-2 rounded-full mb-6">
            <Zap className="w-4 h-4" />
            Atendimento rápido — Ribeirão Preto e Região
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Pronto para ter uma{' '}
            <span className="text-amber-400">hospedagem segura para sua empresa</span>?
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            Fale agora pelo WhatsApp e receba uma proposta personalizada em minutos.
            Migração gratuita e configuração completa inclusa.
          </p>
          <a
            href={WHATSAPP_HOSTING}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hosting-final-cta"
            className="inline-flex items-center gap-2 bg-amber-400 text-slate-950 font-bold px-10 py-4 rounded-xl shadow-[0_0_32px_rgba(251,191,36,0.35)] hover:shadow-[0_0_48px_rgba(251,191,36,0.5)] hover:bg-amber-300 transition-all duration-300 text-base"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Quero Hospedar Meu Site
          </a>

          {/* SEO local footer */}
          <p className="text-slate-600 text-xs mt-10 leading-relaxed">
            <strong className="text-slate-500">FERNANDO TECH</strong> – Especialista em Hospedagem de Site Profissional em Ribeirão Preto.
            Atendimento para empresas, com foco em estabilidade, segurança e suporte técnico.
            Hospedagem empresarial · E-mail corporativo · Servidor confiável · Ribeirão Preto e Região.
          </p>
        </div>
      </div>
      {showLearnMore && <SaibaMais to="/hospedagem" label="Saiba mais sobre Hospedagem de Sites" />}
    </section>
  );
}
